import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { Icon } from '../../components/Icon';
import { SkeletonText } from '../../components/Skeleton';
import { formatDateTime } from '../../i18n';
import { useSession } from '../auth/queries';
import { useCustomerFile } from '../crm/queries';
import type { ArticleStatus } from './api';
import { ArticlePane } from './ArticlePane';
import { CategoryManager } from './CategoryManager';
import { ARTICLE_STATUSES, toneForStatus, toneForVisibility } from './presentation';
import { useArticleSearch, useKbCategories, useRemindReviews } from './queries';
import { Select } from '../../components/Select';

/**
 * Die Wissensdatenbank -- F4-09 bis F4-16.
 *
 * **Links suchen, rechts lesen.** Kein eigener Zweig je Artikel: Wer eine
 * Loesung sucht, springt zwischen Treffern hin und her, und ein Seitenwechsel
 * je Treffer machte daraus eine Wanderung. Die Trefferliste bleibt deshalb
 * stehen, waehrend rechts gerollt wird.
 *
 * **Die Suche wird beim Tippen ausgefuehrt**, anders als die Ticketsuche. Der
 * Unterschied ist bewusst: Ein Wissensbestand hat Hunderte Eintraege, keine
 * Hunderttausende, und wer eine Loesung sucht, weiss selten das genaue Wort.
 * Die vorherige Trefferliste bleibt dabei stehen, statt zu flackern.
 *
 * **Gesetzte Filter stehen als abwaehlbare Marken da.** Ein Filter, den man
 * nur im zugeklappten Auswahlfeld sieht, erklaert eine leere Trefferliste
 * nicht -- und eine leere Trefferliste ohne Erklaerung fuehrt zu der Frage,
 * ob der Artikel geloescht wurde.
 *
 * **Was ungesichert ist, wird nicht stillschweigend verworfen.** Vorher
 * genuegte ein Klick auf einen anderen Treffer, um zehn Minuten Arbeit
 * loszuwerden. Der Wechsel fragt jetzt nach; die Auskunft darueber, ob etwas
 * offen ist, kommt aus dem Artikelbereich (``onDirtyChange``).
 */
export function KnowledgePage({ customerId }: { customerId?: string } = {}) {
  const { t, i18n } = useTranslation();
  const session = useSession();
  // Der Kundenname im Kopf, wenn die Ansicht auf einen Kunden vorgefiltert
  // ist. Ohne ihn saehe die Liste aus wie der ganze Bestand, waere aber nur
  // ein Ausschnitt -- die gefaehrlichere Haelfte einer Filterung.
  const customer = useCustomerFile(customerId ?? '');

  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState<ArticleStatus | ''>('');
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);

  // Ob im Artikelbereich etwas ungesichert offensteht. Als Ref und nicht als
  // Zustand: Es steuert eine Rueckfrage und keine Darstellung -- ein
  // Darstellungslauf je Tastendruck im Titelfeld waere Aufwand fuer nichts.
  const dirtyRef = useRef(false);
  const listRef = useRef<HTMLUListElement>(null);

  // Feste Kennung: Der Artikelbereich meldet den Stand aus einem Effekt
  // heraus. Eine bei jedem Durchlauf neu erzeugte Funktion liesse diesen
  // Effekt bei jedem Tastendruck im Titelfeld erneut laufen.
  const merkeStand = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const categories = useKbCategories();
  const remind = useRemindReviews();
  const query = useArticleSearch({
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(categoryId ? { category_id: categoryId } : {}),
    ...(status ? { status } : {}),
    ...(customerId ? { customer_id: customerId } : {}),
  });

  const canWrite = session.data?.permissions.includes('knowledge.write') ?? false;
  const articles = (query.data?.pages ?? []).flatMap((page) => page.items);
  const gefiltert = search.trim() !== '' || categoryId !== '' || status !== '';

  /** Vor jedem Wechsel: Was offen ist, geht nicht ohne Nachfrage verloren. */
  const darfWechseln = () => !dirtyRef.current || window.confirm(t('knowledge.discardConfirm'));

  const waehlen = (articleId: string) => {
    if (!darfWechseln()) return;
    dirtyRef.current = false;
    setCreating(false);
    setEditing(false);
    setSelected(articleId);
  };

  /**
   * Pfeiltasten in der Trefferliste.
   *
   * Wer dreissig Treffer durchsieht, soll nicht dreissigmal zur Maus greifen.
   * Der Fokus wandert mit und die Auswahl mit ihm -- so steht rechts immer
   * der Artikel, auf dem links der Fokus liegt.
   */
  const beiTaste = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const knoepfe = Array.from(
      listRef.current?.querySelectorAll<HTMLElement>('.result__title') ?? [],
    );
    if (knoepfe.length === 0) return;

    event.preventDefault();
    const jetzt = knoepfe.indexOf(document.activeElement as HTMLElement);
    const richtung = event.key === 'ArrowDown' ? 1 : -1;
    const naechster = Math.min(Math.max(jetzt + richtung, 0), knoepfe.length - 1);
    const ziel = knoepfe[naechster];
    const artikel = articles[naechster];
    if (!ziel || !artikel) return;
    ziel.focus();
    waehlen(artikel.id);
  };

  const filterZuruecksetzen = () => {
    setSearch('');
    setCategoryId('');
    setStatus('');
  };

  const kategorieName =
    (categories.data ?? []).find((item) => item.id === categoryId)?.name ?? categoryId;

  return (
    <>
      <PageHeader
        breadcrumb={
          customerId ? [customer.data?.customer.name ?? '…', t('knowledge.title')] : undefined
        }
        title={t('knowledge.title')}
        count={
          articles.length > 0
            ? query.hasNextPage
              ? t('knowledge.countMore', { count: articles.length })
              : t('knowledge.count', { count: articles.length })
            : undefined
        }
        subtitle={
          customerId
            ? t('knowledge.forCustomer', {
                name: customer.data?.customer.name ?? '…',
              })
            : t('knowledge.subtitle')
        }
        action={
          canWrite ? (
            <div className="row">
              {/* Ein Artikel ueber eine Anlage, die es nicht mehr gibt, faellt
                  sonst erst dem auf, der danach handelt. Der Lauf legt **ein**
                  Ticket fuer alle faelligen Artikel an -- F4-16. */}
              <Button
                busy={remind.isPending}
                onClick={() => {
                  remind.mutate();
                }}
              >
                {t('knowledge.remindReviews')}
              </Button>
              {remind.data ? (
                <span className="field__hint" role="status">
                  {t('knowledge.remindResult', { count: remind.data.reminded })}
                </span>
              ) : null}
              <CategoryManager />
              <Button
                variant="primary"
                onClick={() => {
                  if (!darfWechseln()) return;
                  dirtyRef.current = false;
                  setSelected(null);
                  setCreating(true);
                  setEditing(true);
                }}
              >
                {t('knowledge.newArticle')}
              </Button>
            </div>
          ) : null
        }
      />

      {remind.isError ? <ErrorNotice error={remind.error} /> : null}

      <div className="kb-workspace">
        <div className="kb-list">
          <div className="kb-search">
            <span className="kb-search__icon">
              <Icon name="search" />
            </span>
            <input
              id="kb-search"
              type="search"
              className="field__control kb-search__control"
              value={search}
              aria-label={t('app.search')}
              placeholder={t('knowledge.searchPlaceholder')}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
            {search ? (
              <button
                type="button"
                className="kb-search__clear"
                aria-label={t('knowledge.clearSearch')}
                onClick={() => {
                  setSearch('');
                }}
              >
                {'×'}
              </button>
            ) : null}
          </div>

          <div className="row">
            <Select
              className="field__control"
              aria-label={t('knowledge.category')}
              value={categoryId}
              onChange={(event) => {
                setCategoryId(event.target.value);
              }}
            >
              <option value="">{t('knowledge.allCategories')}</option>
              {(categories.data ?? []).map((category) => (
                <option key={category.id} value={category.id}>
                  {'  '.repeat(category.depth)}
                  {category.name}
                </option>
              ))}
            </Select>

            <Select
              className="field__control"
              aria-label={t('knowledge.status')}
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as ArticleStatus | '');
              }}
            >
              <option value="">{t('knowledge.allStatuses')}</option>
              {ARTICLE_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`kbStatus.${value}`)}
                </option>
              ))}
            </Select>
          </div>

          {gefiltert ? (
            <div className="kb-chips">
              {search.trim() ? (
                <button
                  type="button"
                  className="kb-chip"
                  onClick={() => {
                    setSearch('');
                  }}
                >
                  {t('knowledge.chipSearch', { query: search.trim() })}
                  <span className="kb-chip__remove" aria-hidden="true">
                    ×
                  </span>
                </button>
              ) : null}
              {categoryId ? (
                <button
                  type="button"
                  className="kb-chip"
                  onClick={() => {
                    setCategoryId('');
                  }}
                >
                  {t('knowledge.chipCategory', { name: kategorieName })}
                  <span className="kb-chip__remove" aria-hidden="true">
                    ×
                  </span>
                </button>
              ) : null}
              {status ? (
                <button
                  type="button"
                  className="kb-chip"
                  onClick={() => {
                    setStatus('');
                  }}
                >
                  {t('knowledge.chipStatus', { name: t(`kbStatus.${status}`) })}
                  <span className="kb-chip__remove" aria-hidden="true">
                    ×
                  </span>
                </button>
              ) : null}
              <Button variant="ghost" onClick={filterZuruecksetzen}>
                {t('knowledge.resetFilters')}
              </Button>
            </div>
          ) : null}

          {query.isError ? <ErrorNotice error={query.error} /> : null}

          <ul
            className="result-list"
            ref={listRef}
            aria-label={t('knowledge.hitsLabel')}
            aria-busy={query.isPending}
            onKeyDown={beiTaste}
          >
            {query.isPending ? (
              <li style={{ padding: 'var(--space-3)' }}>
                <SkeletonText lines={8} />
              </li>
            ) : null}

            {query.isSuccess && articles.length === 0 ? (
              <li>
                <EmptyState
                  icon={gefiltert ? 'search' : 'knowledge'}
                  title={gefiltert ? t('knowledge.noResults') : t('knowledge.empty')}
                  hint={gefiltert ? t('knowledge.emptyHint') : t('knowledge.emptyStock')}
                  action={
                    gefiltert ? (
                      <Button onClick={filterZuruecksetzen}>{t('knowledge.resetFilters')}</Button>
                    ) : null
                  }
                />
              </li>
            ) : null}

            {articles.map((article) => (
              <li
                key={article.id}
                className={selected === article.id ? 'result result--current' : 'result'}
              >
                {/* Der Titel ist die Schaltflaeche, seine aufgespannte
                    Klickflaeche deckt die ganze Karte. Warum nicht die Karte
                    selbst der Knopf ist, steht bei ``.result`` in
                    components.css: Ihr Name waere dann der gesamte Text. */}
                <button
                  type="button"
                  className="result__title"
                  aria-current={selected === article.id ? 'true' : undefined}
                  onClick={() => {
                    waehlen(article.id);
                  }}
                >
                  {article.title}
                </button>

                {article.summary ? (
                  <span className="result__summary">{article.summary}</span>
                ) : null}

                {/* Jede Angabe in einem eigenen Element. Nebeneinander in
                    einem Textknoten waeren sie ein Satz, den weder ein
                    vorlesendes Programm noch eine Pruefung auseinanderhaelt
                    -- und "Portal" hiesse dann "Freigegeben Portal Gueltig
                    fuer alle Kunden Geaendert gestern". */}
                <span className="result__meta">
                  <span className="result__fact">
                    <span className={`result__dot result__dot--${toneForStatus(article.status)}`} />
                    {t(`kbStatus.${article.status}`)}
                  </span>
                  <span className="result__fact">
                    <span
                      className={`result__dot result__dot--${toneForVisibility(article.visibility)}`}
                    />
                    {t(`kbVisibility.${article.visibility}`)}
                  </span>
                  {/* Der Kundenbezug gehoert in die Liste -- F4-25. Ohne ihn
                      sieht ein kundenspezifischer Artikel aus wie ein
                      allgemeiner, und beim Suchen ist das der Unterschied
                      zwischen einer Antwort und einer falschen Antwort. */}
                  <span className="result__fact">
                    {(article.customer_ids ?? []).length === 0
                      ? t('knowledge.customersAll')
                      : t('knowledge.customersSelected', {
                          count: (article.customer_ids ?? []).length,
                        })}
                  </span>
                  <span className="result__fact">
                    {t('knowledge.updatedAt', {
                      moment: formatDateTime(article.updated_at, i18n.language),
                    })}
                  </span>
                </span>
              </li>
            ))}
          </ul>

          {/* Nachladen statt Seitenzahlen -- F4-27. Wer in einem
              Wissensbestand sucht, blaettert nicht zu "Seite 4", sondern will
              mehr vom selben. Der Filterzustand bleibt dabei stehen; er steckt
              im Schluessel der Abfrage. */}
          {query.hasNextPage ? (
            <div className="kb-list__foot">
              <Button
                busy={query.isFetchingNextPage}
                onClick={() => {
                  void query.fetchNextPage();
                }}
              >
                {t('knowledge.loadMore')}
              </Button>
              <span>{t('knowledge.loadedCount', { count: articles.length })}</span>
            </div>
          ) : null}
        </div>

        {creating || selected !== null ? (
          <ArticlePane
            articleId={selected}
            canWrite={canWrite}
            editing={editing}
            presetCustomerId={customerId}
            onEditingChange={setEditing}
            onDirtyChange={merkeStand}
            onSaved={(articleId) => {
              dirtyRef.current = false;
              setCreating(false);
              setSelected(articleId === '' ? null : articleId);
            }}
          />
        ) : (
          <div className="kb-detail">
            <EmptyState
              icon="knowledge"
              title={t('knowledge.selectArticle')}
              hint={t('knowledge.selectArticleHint')}
              action={
                canWrite ? (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setSelected(null);
                      setCreating(true);
                      setEditing(true);
                    }}
                  >
                    {t('knowledge.startArticle')}
                  </Button>
                ) : null
              }
            />
          </div>
        )}
      </div>
    </>
  );
}
